/**
 * Container for the shared player search results list.
 *
 * Provides memoized render callbacks and guarded pagination to the view.
 * Uses PlayerSearchResultRow with a caller-supplied `renderAction` callback
 * for the action slot, enabling different behaviors per consumer:
 * - GlobalSearchModal: chevron icon for navigation
 * - AddPlayerSearchSurface: Add/Added buttons for watchlist management
 * Delegates virtualization and end-reached detection to FlashList in the view.
 * @module components/shared/search-modal/PlayerSearchResultsList/PlayerSearchResultsListContainer
 */

import React, { memo, useCallback, useMemo } from "react";

import PlayerSearchResultRow from "@/components/organisms/shared/search-modal/PlayerSearchResultRow";
import type { NoPlayersFoundActionPlacement } from "@/components/organisms/shared/search-modal/NoPlayersFound/NoPlayersFoundView";
import { type ListRenderItem } from "@/components/atoms/FlashList";
import { PlayerSearchPlayerFragment } from "@/generated/graphql";

import PlayerSearchResultsListView from "./PlayerSearchResultsListView";

/**
 * Props for PlayerSearchResultsListContainer
 */
interface PlayerSearchResultsListContainerProps {
  /** Player results to display */
  readonly players: readonly PlayerSearchPlayerFragment[];
  /** Whether data is loading */
  readonly loading: boolean;
  /**
   * Whether a NEW query is in flight while the previous results are still
   * displayed — drives the inline refresh strip instead of the full-panel
   * spinner (SE-7374).
   */
  readonly isRefreshing?: boolean;
  /** Triggers next page fetch */
  readonly loadMore: () => void;
  /** Whether more pages are available */
  readonly hasMore: boolean;
  /** Whether a fetchMore request is currently in progress */
  readonly isLoadingMore: boolean;
  /** Whether results are empty */
  readonly isEmpty: boolean;
  /** Whether the search query returned an error */
  readonly isError: boolean;
  /** Called when a player row is pressed */
  readonly onPlayerPress: (item: PlayerSearchPlayerFragment) => void;
  /** Active sort field for metric badge display */
  readonly effectiveSortBy: string;
  /** Display label for the sort field (e.g. "GPR", "Style Fit") */
  readonly sortFieldLabel: string;
  /** Optional ManuallyAddPlayer option rendered in the empty state */
  readonly manuallyAddPlayerOption?: React.ReactNode;
  /** Optional ManuallyAddPlayer option rendered as a list footer when results exist */
  readonly manuallyAddPlayerFooterOption?: React.ReactNode;
  /** Renders the action slot for each player row (e.g. chevron, Add/Added button) */
  readonly renderAction: (
    player: PlayerSearchPlayerFragment
  ) => React.ReactNode;
  /** Optional subtitle override forwarded to the empty state */
  readonly emptySubtitle?: string;
  /** Optional heading override forwarded to the empty state (SE-8324) */
  readonly emptyHeading?: string;
  /**
   * Whether a given row accepts a press. Defaults to "every row does", which
   * is what every caller before SE-7242 relied on.
   *
   * A row this returns `false` for is given NO press handler at all, rather
   * than a handler that no-ops: the row view drops its cursor and hover
   * affordance when it has no handler, so an inert row reads as inert. Quick
   * capture uses it for players already on the match report.
   */
  readonly isRowPressable?: (player: PlayerSearchPlayerFragment) => boolean;
  /** Where the empty state's action sits relative to its message */
  readonly emptyActionPlacement?: NoPlayersFoundActionPlacement;
  /**
   * Whether this list may own a virtualized scroller. Defaults to true.
   * A host that is itself a scroller passes false — see the view's comment
   * on the plain-rows path.
   */
  readonly isVirtualized?: boolean;
}

/**
 * Container for the shared player search results list.
 * @param props - Component props
 * @param props.players - Player results
 * @param props.loading - Loading state
 * @param props.isRefreshing - Whether a newer query is in flight over existing results
 * @param props.loadMore - Load more callback
 * @param props.hasMore - Whether more pages are available
 * @param props.isLoadingMore - Whether a fetchMore request is in progress
 * @param props.isEmpty - Empty state flag
 * @param props.isError - Whether the search query returned an error
 * @param props.onPlayerPress - Player press callback
 * @param props.effectiveSortBy - Active sort field
 * @param props.sortFieldLabel - Sort field display label
 * @param props.manuallyAddPlayerOption - Optional ManuallyAddPlayer option for empty state
 * @param props.manuallyAddPlayerFooterOption - Optional ManuallyAddPlayer option for list footer
 * @param props.renderAction - Action slot renderer per player row
 * @param props.emptySubtitle - Optional subtitle override forwarded to the empty state
 * @param props.emptyHeading - Optional heading override forwarded to the empty state
 * @param props.isRowPressable - Whether a given row accepts a press (default: all do)
 * @param props.emptyActionPlacement - Where the empty state's action sits
 * @param props.isVirtualized - Whether this list may own a scroller
 */
const PlayerSearchResultsListContainer = ({
  players,
  loading,
  isRefreshing,
  loadMore,
  hasMore,
  isLoadingMore,
  isEmpty,
  isError,
  onPlayerPress,
  effectiveSortBy,
  sortFieldLabel,
  manuallyAddPlayerOption,
  manuallyAddPlayerFooterOption,
  renderAction,
  emptySubtitle,
  emptyHeading,
  isRowPressable,
  emptyActionPlacement,
  isVirtualized,
}: PlayerSearchResultsListContainerProps) => {
  /**
   * Pre-built press handlers keyed by player ID.
   *
   * A row `isRowPressable` rejects gets no entry, so the row view receives
   * `onPress === undefined` and renders inert rather than pressable-looking.
   */
  const pressHandlers = useMemo(
    () =>
      players.reduce(
        (acc, player) =>
          isRowPressable && !isRowPressable(player)
            ? acc
            : {
                ...acc,
                [player.id]: () => onPlayerPress(player),
              },
        {} as Record<string, () => void>
      ),
    [players, onPlayerPress, isRowPressable]
  );

  /**
   * One row, independent of how the list is being laid out. Both the
   * virtualized and the plain path render through this so the two cannot
   * drift apart.
   */
  const renderRow = useCallback(
    (player: PlayerSearchPlayerFragment) => (
      <PlayerSearchResultRow
        player={player}
        effectiveSortBy={effectiveSortBy}
        sortFieldLabel={sortFieldLabel}
        onPress={pressHandlers[player.id]}
        action={renderAction(player)}
      />
    ),
    [effectiveSortBy, sortFieldLabel, pressHandlers, renderAction]
  );

  // Returns the row ELEMENT itself, not a fragment around it. A wrapper here
  // would be invisible on screen but would move the row's props one level
  // down, which is a contract change for anything reading them off what
  // `renderItem` returns — the existing suite does exactly that.
  const renderItem: ListRenderItem<PlayerSearchPlayerFragment> = useCallback(
    ({ item }) => renderRow(item),
    [renderRow]
  );

  /** Guarded end-reached handler — skips if already loading or no more pages */
  const handleEndReached = useCallback(() => {
    if (loading || isLoadingMore || !hasMore) return;
    loadMore();
  }, [loading, isLoadingMore, hasMore, loadMore]);

  return (
    <PlayerSearchResultsListView
      players={players}
      loading={loading}
      isRefreshing={isRefreshing}
      isEmpty={isEmpty}
      isError={isError}
      isLoadingMore={isLoadingMore}
      renderItem={renderItem}
      renderRow={renderRow}
      isVirtualized={isVirtualized}
      onEndReached={handleEndReached}
      manuallyAddPlayerOption={manuallyAddPlayerOption}
      manuallyAddPlayerFooterOption={manuallyAddPlayerFooterOption}
      emptySubtitle={emptySubtitle}
      emptyHeading={emptyHeading}
      emptyActionPlacement={emptyActionPlacement}
    />
  );
};

export default memo(PlayerSearchResultsListContainer);
